/**
 * useOtherUserProfile — loads another member's profile by GUID.
 *
 * GET /UserProfile/GetOtherUserProfile answers 200 with `isAccessible: false`
 * (or `success: false`) when the owner's privacy setting blocks the viewer, so
 * a restriction is not an HTTP error and has to be normalised here rather than
 * in a screen.
 */
import { useCallback, useEffect, useState } from 'react';
import profileService from '../api/services/profileService';
import { mapApiProfileToProfile } from '../utils/userProfileMapper';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { getProfileRestrictionReason, resolveProfileRestriction } from '../utils/profileVisibility';
import { normalizeUserGuid } from '../utils/navigateToUserProfile';
import type { ProfileRestrictionReason } from '../utils/profileVisibility';
import type { ApiError } from '../types/api';
import type { Profile } from '../types/profile';
import type { UserProfileResponse } from '../types/userProfile';

/**
 * Keeps the public identity and relationship fields an inaccessible-profile
 * response may expose, without retaining its private profile content.
 */
export const mapRestrictedProfileIdentity = (
  raw: UserProfileResponse | null | undefined,
): Profile | null => {
  if (!raw) return null;
  const hasIdentity = Boolean(
    raw.userId
    || raw.userGuid
    || raw.firstName?.trim()
    || raw.lastName?.trim()
    || raw.userName?.trim(),
  );
  if (!hasIdentity) return null;

  const mapped = mapApiProfileToProfile(raw);
  return {
    id: mapped.id,
    userId: mapped.userId,
    userGuid: mapped.userGuid,
    name: mapped.name,
    firstName: mapped.firstName,
    lastName: mapped.lastName,
    userName: mapped.userName,
    avatar: mapped.avatar,
    profilePicturePrivacy: mapped.profilePicturePrivacy,
    profileViewPrivacy: mapped.profileViewPrivacy,
    isBuddy: mapped.isBuddy,
    invited: mapped.invited,
    isRequested: mapped.isRequested,
    isAccepted: mapped.isAccepted,
    isFollowing: mapped.isFollowing,
    status: mapped.status,
    // The buddy count is public (Facebook-style); other stats stay private.
    buddiesCount: mapped.buddiesCount,
  };
};

export interface UseOtherUserProfileResult {
  profile: Profile | null;
  loading: boolean;
  /** Transport/server failure message. Empty when the load succeeded. */
  error: string;
  /** Set when the profile exists but the viewer may not see it. */
  restriction: ProfileRestrictionReason | null;
  refetch: () => void;
}

export const useOtherUserProfile = (
  profileUserGuid: string | null | undefined,
): UseOtherUserProfileResult => {
  const guid = normalizeUserGuid(profileUserGuid);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(Boolean(guid));
  const [error, setError] = useState('');
  const [restriction, setRestriction] = useState<ProfileRestrictionReason | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refetch = useCallback(() => setReloadToken(token => token + 1), []);

  useEffect(() => {
    if (!guid) {
      setProfile(null);
      setLoading(false);
      setRestriction('unavailable');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setRestriction(null);

    profileService
      .getOtherUserProfile(guid)
      .then(response => {
        if (cancelled) return;
        const body = response?.data;

        if (!body || body.success === false || body.isAccessible === false) {
          // Some inaccessible responses still include a public identity subset.
          // Preserve only that subset for the restricted-profile header.
          setProfile(mapRestrictedProfileIdentity(body));
          // The body may still carry profileViewPrivacy + isBuddy even when
          // inaccessible, so we can distinguish "Buddies only" from "Private".
          const reason = getProfileRestrictionReason(
            body?.profileViewPrivacy,
            { isBuddy: body?.isBuddy },
          );
          setRestriction(reason ?? 'unavailable');
          setLoading(false);
          return;
        }

        const mapped = mapApiProfileToProfile(body);
        // The server is the authority: an explicit isAccessible: true means
        // the viewer (e.g. a buddy of a "Private" profile) may open it. The
        // local rule only runs when the payload omits the flag.
        setRestriction(
          resolveProfileRestriction(body.isAccessible, mapped.profileViewPrivacy, {
            isBuddy: mapped.isBuddy,
          }),
        );
        setProfile(mapped);
        setLoading(false);
      })
      .catch(caught => {
        if (cancelled) return;
        setProfile(null);
        const status = (caught as ApiError & { response?: { status?: number } })
          ?.response?.status;
        if (status === 404) {
          setRestriction('unavailable');
        } else {
          setError(getApiErrorMessage(caught as ApiError));
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [guid, reloadToken]);

  return { profile, loading, error, restriction, refetch };
};

export default useOtherUserProfile;
